
import { useState, useEffect, useCallback } from 'react';
import { studentService } from '@/services/db/students';
import { getTeachers } from '@/services/db/teachers';
import { getAdmins } from '@/services/db/admins';
import { getStudents } from '@/services/db/students';
import { useStudents } from '@/contexts';
import { useQuery } from '@tanstack/react-query';
import { enhancedDocumentStorage } from '@/services/storage/enhancedDocumentStorage';
import { Student } from '@/services/db/types';

export interface DashboardStats {
  totalStudents: number;
  totalTeachers: number;
  totalAdmins: number;
  totalDocuments: number;
  totalRevenue: number;
  monthlyRevenue: number;
  pendingPayments: number;
  activePrograms: number;
  certificatesIssued: number;
  totalPrograms: number;
  totalFees: number;
  recentEnrollments: number;
  recentActivities: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
}

export const useDashboardStats = () => {
  const { students } = useStudents();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [programData, setProgramData] = useState<any[]>([]);
  const [enrollmentData, setEnrollmentData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Real-time queries for accurate data
  const { data: realtimeStudents = [], refetch: refetchStudents } = useQuery({
    queryKey: ['dashboard-students'],
    queryFn: getStudents,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: true
  });

  const { data: realtimeTeachers = [], refetch: refetchTeachers } = useQuery({
    queryKey: ['dashboard-teachers'],
    queryFn: getTeachers,
    refetchInterval: 30000,
    refetchOnWindowFocus: true
  });

  const { data: realtimeAdmins = [], refetch: refetchAdmins } = useQuery({
    queryKey: ['dashboard-admins'],
    queryFn: getAdmins,
    refetchInterval: 30000,
    refetchOnWindowFocus: true
  });

  const calculateStats = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 Calculating dashboard stats with real-time data...');
      
      // Use real-time data from queries with proper type checking
      const studentsData = Array.isArray(realtimeStudents) && realtimeStudents.length > 0 ? realtimeStudents : students;
      const teachersData = Array.isArray(realtimeTeachers) ? realtimeTeachers : [];
      const adminsData = Array.isArray(realtimeAdmins) ? realtimeAdmins : [];
      
      // Get document stats from enhanced storage
      const documentStats = enhancedDocumentStorage.getStorageStats();
      
      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);
      
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      let totalRevenue = 0;
      let monthlyRevenue = 0;
      let pendingPayments = 0;
      let totalPrograms = 0;
      let recentEnrollments = 0;
      
      if (Array.isArray(studentsData)) {
        studentsData.forEach((student: Student) => {
          // Fix: Use createdAt instead of registrationDate
          if (new Date(student.createdAt) >= thirtyDaysAgo) {
            recentEnrollments++;
          }
          
          if (student.programs && Array.isArray(student.programs)) {
            student.programs.forEach(program => {
              totalPrograms++;
              const fee = program.fee || 0;
              
              if (program.feeStatus === 'paid' || program.feeStatus === 'partial') {
                totalRevenue += fee;
                
                const enrollDate = new Date(program.enrolledAt);
                if (enrollDate >= startOfMonth) {
                  monthlyRevenue += fee;
                }
              }
              
              if (program.feeStatus === 'pending') {
                pendingPayments += fee;
              }
            });
          }
        });
      }

      // Generate program distribution data
      const programCounts: Record<string, number> = {};
      if (Array.isArray(studentsData)) {
        studentsData.forEach((student: Student) => {
          if (student.programs && Array.isArray(student.programs)) {
            student.programs.forEach(program => {
              // Fix: Use title instead of name
              programCounts[program.title] = (programCounts[program.title] || 0) + 1;
            });
          }
        });
      }

      const programDistribution = Object.entries(programCounts).map(([name, count]) => ({
        name,
        value: count,
        percentage: ((count / totalPrograms) * 100).toFixed(1)
      }));

      // Generate enrollment trends (last 6 months)
      const enrollmentTrends = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthName = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        
        const enrollmentsInMonth = Array.isArray(studentsData) ? studentsData.filter((student: Student) => {
          // Fix: Use createdAt instead of registrationDate
          const studentDate = new Date(student.createdAt);
          return studentDate.getMonth() === date.getMonth() && 
                 studentDate.getFullYear() === date.getFullYear();
        }).length : 0;
        
        enrollmentTrends.push({
          month: monthName,
          enrollments: enrollmentsInMonth
        });
      }

      const dashboardStats: DashboardStats = {
        totalStudents: Array.isArray(studentsData) ? studentsData.length : 0,
        totalTeachers: Array.isArray(teachersData) ? teachersData.length : 0,
        totalAdmins: Array.isArray(adminsData) ? adminsData.length : 0,
        totalDocuments: documentStats.totalDocuments,
        totalRevenue,
        monthlyRevenue,
        pendingPayments,
        activePrograms: totalPrograms,
        certificatesIssued: 0, // TODO: Implement certificate counting
        totalPrograms,
        totalFees: totalRevenue + pendingPayments,
        recentEnrollments,
        recentActivities: [] // TODO: Implement activity tracking
      };

      console.log('📊 Dashboard stats calculated:', dashboardStats);
      console.log(`📄 Including ${documentStats.totalDocuments} documents in stats`);
      
      setStats(dashboardStats);
      setProgramData(programDistribution);
      setEnrollmentData(enrollmentTrends);
    } catch (error) {
      console.error('Error calculating dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [students, realtimeStudents, realtimeTeachers, realtimeAdmins]);

  // Force refresh function
  const forceRefresh = useCallback(async () => {
    console.log('🔄 Force refreshing dashboard stats...');
    await Promise.all([
      refetchStudents(),
      refetchTeachers(),
      refetchAdmins()
    ]);
    await calculateStats();
  }, [calculateStats, refetchStudents, refetchTeachers, refetchAdmins]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing dashboard stats...');
      calculateStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [calculateStats]);

  return {
    stats,
    programData,
    enrollmentData,
    loading,
    forceRefresh
  };
};
